import { Offscreen } from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AnnouncementSeverity } from '~/common/api-client';
import Link from '~/common/components/Link';
import { normalizeAnnouncementLinkForDisplay } from './announcementsDisplay';

/**
 * Spoken severity prefixes. Severity is otherwise conveyed only by the toast's
 * colour and decorative icon, so screen reader users need it spelled out.
 */
export const useSeverityPrefixes = (): Record<AnnouncementSeverity, string> => {
  const { t } = useTranslation();
  return {
    informational: t('Informational notice:'),
    warning: t('Warning notice:'),
    critical: t('Critical notice:')
  };
};

interface AnnouncementToastContentProps {
  severity: AnnouncementSeverity;
  description: string;
  link: string | null;
  linkText: string | null;
}

/**
 * The body of an announcement toast, shared by the live consumer toasts and the
 * admin form's preview so the preview can never drift from what ships.
 *
 * The link is normalised before rendering: only http(s) values become an anchor
 * (an unsaved `javascript:` URL in the preview stays plain text), and the shared
 * `Link` adds the external-link icon and "Opens in a new tab" text.
 */
const AnnouncementToastContent: React.FC<AnnouncementToastContentProps> = ({
  severity,
  description,
  link,
  linkText
}) => {
  const severityPrefixes = useSeverityPrefixes();
  const safeLink = normalizeAnnouncementLinkForDisplay(link);

  return (
    <>
      <Offscreen>{severityPrefixes[severity]} </Offscreen>
      {description}
      {safeLink && (
        <>
          {' '}
          <Link url={safeLink} openInNewTab>
            {linkText || safeLink}
          </Link>
        </>
      )}
    </>
  );
};

export default AnnouncementToastContent;
